import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const AGENT_META = {
  projectLeader:  { name: "Líder de Proyecto",        icon: "⭐", color: "#F59E0B" },
  strategist:     { name: "Estratega de Cliente",     icon: "🎯", color: "#0b00f9" },
  onboarding:     { name: "Analista de Onboarding",   icon: "🔍", color: "#6366f1" },
  copywriter:     { name: "Copywriter",               icon: "✍️", color: "#ec4899" },
  contentPlanner: { name: "Planificador de Contenido", icon: "📅", color: "#14b8a6" },
  adsSpecialist:  { name: "Especialista en Ads",      icon: "📢", color: "#f59e0b" },
  seoAnalyst:     { name: "Analista SEO",             icon: "🔎", color: "#22c55e" },
  reportAnalyst:  { name: "Analista de Reportes",     icon: "📊", color: "#a855f7" },
  upselling:      { name: "Agente de Upselling",      icon: "💡", color: "#f97316" },
  competitive:    { name: "Analista Competitivo",     icon: "🏆", color: "#ef4444" },
  qa:             { name: "QA de Contenido",          icon: "✅", color: "#64748b" },
  uxWriter:       { name: "UX Writer",               icon: "🖊️", color: "#8b5cf6" },
  meetingMinutes: { name: "Minutas de Reunión",       icon: "🗒️", color: "#0ea5e9" },
  pm:             { name: "Project Manager",          icon: "📬", color: "#0ea5e9" },
};

// Builds a clear error message for file upload failures, distinguishing
// app-level errors (JSON body with .error) from infra-level failures
// (nginx 413, timeouts, network errors) that return non-JSON bodies.
function describeUploadError(err, filename) {
  const apiError = err.response?.data?.error;
  if (apiError) return apiError;

  if (err.response?.status === 413) {
    return `"${filename}" es demasiado grande para el servidor. Contactá a soporte técnico.`;
  }
  if (err.response) {
    return `Error al subir "${filename}" (código ${err.response.status}). Probá de nuevo o contactá a soporte técnico.`;
  }
  if (err.code === "ECONNABORTED") {
    return `"${filename}" tardó demasiado en subirse. Probá con un archivo más chico o de nuevo en unos segundos.`;
  }
  return `Error de conexión al subir "${filename}". Verificá tu conexión e intentá de nuevo.`;
}

// Parse last ---EMAIL--- block from messages for the PM agent
function parseEmailBlock(msgs) {
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m.role !== "assistant") continue;
    const match = m.content.match(/---EMAIL---\r?\n([\s\S]*?)\r?\n---FIN EMAIL---/);
    if (match) {
      const emailText = match[1];
      const subjectMatch = emailText.match(/^Asunto:\s*(.+)$/m);
      const subject = subjectMatch ? subjectMatch[1].trim() : "(sin asunto)";
      const body = emailText.replace(/^Asunto:.*\n\n?/, "").trim();
      return { subject, body };
    }
  }
  return null;
}

function exportContent(content, format, agentName, clientName) {
  const filename = `${agentName}_${clientName}_${new Date().toLocaleDateString("es-AR").replace(/\//g, "-")}`;

  if (format === "txt") {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.txt`; a.click();
    URL.revokeObjectURL(url);
    return;
  }
  if (format === "md") {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.md`; a.click();
    URL.revokeObjectURL(url);
    return;
  }
  if (format === "html") {
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${filename}</title>
<style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:0 24px;line-height:1.7;color:#1a1a1a}
h1,h2,h3{color:#0b00f9}pre{background:#f5f5f5;padding:16px;border-radius:8px}
code{background:#f5f5f5;padding:2px 6px;border-radius:4px}</style></head>
<body><h1>${agentName} — ${clientName}</h1><p style="color:#666">${new Date().toLocaleString("es-AR")}</p>
<hr><div>${content.replace(/\n/g, "<br>")}</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.html`; a.click();
    URL.revokeObjectURL(url);
    return;
  }
  if (format === "doc") {
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:w="urn:schemas-microsoft-com:office:word"
xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"><title>${filename}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
<style>body{font-family:Arial;font-size:12pt;line-height:1.6;color:#1a1a1a}
h1,h2,h3{color:#0b00f9}p{margin:0 0 10pt 0}</style></head>
<body>
<h1>${agentName} — ${clientName}</h1>
<p style="color:#666">${new Date().toLocaleString("es-AR")}</p><hr>
${content.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")}
</body></html>`;
    const blob = new Blob(["\ufeff" + html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.doc`; a.click();
    URL.revokeObjectURL(url);
    return;
  }
  if (format === "csv") {
    // If content has markdown tables, extract and convert them to CSV.
    // Otherwise export each non-empty line as a row.
    const tableRegex = /(\|[^\n]+\|\n)((?:\|[-| :]+\|\n))((?:\|[^\n]+\|\n?)*)/g;
    const tables = [...content.matchAll(tableRegex)];

    let csvContent = "";
    if (tables.length > 0) {
      // Use the first (or largest) table found
      const largest = tables.reduce((a, b) => b[0].length > a[0].length ? b : a);
      const lines = largest[0].trim().split("\n").filter(l => !/^\|[-| :]+\|$/.test(l.trim()));
      const rows = lines.map(line =>
        line.split("|").slice(1, -1).map(cell => {
          const t = cell.trim().replace(/\*\*/g, "").replace(/\*/g, "");
          return t.includes(",") || t.includes('"') || t.includes("\n")
            ? `"${t.replace(/"/g, '""')}"` : t;
        }).join(",")
      );
      csvContent = rows.join("\n");
    } else {
      // No tables — one row per non-empty line, single "Contenido" column
      const lines = content.split("\n").filter(l => l.trim());
      csvContent = "Contenido\n" + lines.map(l => {
        const t = l.replace(/\*\*/g, "").replace(/^#+\s*/, "").trim();
        return t.includes(",") || t.includes('"')
          ? `"${t.replace(/"/g, '""')}"` : t;
      }).join("\n");
    }

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
    return;
  }
  if (format === "pdf") {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${filename}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:760px;margin:40px auto;padding:0 24px;line-height:1.7;color:#1a1a1a;font-size:13px}
  h1{font-size:20px;color:#0b00f9;margin-bottom:4px}h2,h3{color:#0b00f9}
  .meta{color:#888;font-size:12px;margin-bottom:16px}hr{border:none;border-top:1px solid #ddd;margin:16px 0}
  @media print{body{margin:20px}}
</style></head>
<body>
  <h1>${agentName} — ${clientName}</h1>
  <div class="meta">${new Date().toLocaleString("es-AR")}</div>
  <hr><div>${content.replace(/\n/g, "<br>")}</div>
  <script>window.onload=function(){window.print();}<\/script>
</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    return;
  }
}

// Searchable client selector
function ClientSearch({ clients, selected, onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (c) => {
    onSelect(c);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 12px", borderRadius: "var(--radius-md)",
          background: "var(--bg-elevated)", border: "1px solid var(--border)",
          cursor: "pointer", fontSize: 13, color: selected ? "var(--text-primary)" : "var(--text-muted)",
          userSelect: "none",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.name : "— Seleccioná un cliente —"}
        </span>
        <span style={{ marginLeft: 8, fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>▾</span>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "var(--bg-surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)", boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          zIndex: 200, overflow: "hidden",
        }}>
          <div style={{ padding: "8px 8px 4px" }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar cliente..."
              style={{
                width: "100%", padding: "6px 10px", borderRadius: "var(--radius-sm)",
                background: "var(--bg-elevated)", border: "1px solid var(--border)",
                color: "var(--text-primary)", fontSize: 12, outline: "none",
                fontFamily: "inherit", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-muted)" }}>
                Sin resultados
              </div>
            ) : filtered.map(c => (
              <div
                key={c.id}
                onClick={() => handleSelect(c)}
                style={{
                  padding: "8px 14px", fontSize: 13, cursor: "pointer",
                  color: selected?.id === c.id ? "var(--accent)" : "var(--text-primary)",
                  background: selected?.id === c.id ? "var(--accent-dim)" : "transparent",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => { if (selected?.id !== c.id) e.currentTarget.style.background = "var(--bg-elevated)"; }}
                onMouseLeave={e => { if (selected?.id !== c.id) e.currentTarget.style.background = "transparent"; }}
              >
                {c.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Message bubble — export always available on assistant messages
// Renders plain text with clickable hyperlinks (for user messages)
function renderTextWithLinks(text, linkStyle = {}) {
  const parts = text.split(/(https?:\/\/[^\s]+)/);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           style={{ wordBreak: "break-all", textDecoration: "underline", ...linkStyle }}>{part}</a>
      : part
  );
}

function MessageBubble({ msg, agent, onExport }) {
  const [copied, setCopied] = React.useState(false);
  const isUser = msg.role === "user";
  // Strip [EXPORTABLE] tag if present (agent hint, but export is always available)
  const displayContent = (msg.content || "").replace("[EXPORTABLE]", "").trim();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = displayContent;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      alignItems: "flex-start",
      gap: 10,
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: "50%", background: agent.color + "22",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, flexShrink: 0, marginTop: 2,
        }}>
          {agent.icon}
        </div>
      )}

      <div className="msg-bubble" style={{ maxWidth: "75%" }}>
        {isUser && msg.attachments?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end", marginBottom: 6 }}>
            {msg.attachments.map((a, i) => (
              a.url ? (
                <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                   title={a.filename}
                   style={{
                     display: "flex", alignItems: "center", gap: 5,
                     background: "var(--bg-elevated)", border: "1px solid var(--border)",
                     borderRadius: "var(--radius-md)", padding: "5px 10px",
                     fontSize: 12, color: "var(--text-secondary)", textDecoration: "none",
                     maxWidth: 220,
                   }}>
                  <span>📎</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                </a>
              ) : (
                <span key={i} title={a.deletedAt ? "Archivo eliminado automáticamente (más de 6 meses)" : a.filename} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: "var(--bg-elevated)", border: "1px dashed var(--border)",
                  borderRadius: "var(--radius-md)", padding: "5px 10px",
                  fontSize: 12, color: "var(--text-muted)",
                  maxWidth: 260,
                }}>
                  <span>📎</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.filename}{a.deletedAt ? " (eliminado — +6 meses)" : ""}
                  </span>
                </span>
              )
            ))}
          </div>
        )}
        <div style={{
          padding: "12px 16px",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          background: isUser ? "var(--accent)" : "var(--bg-elevated)",
          color: isUser ? "#fff" : "var(--text-primary)",
          fontSize: 14,
          lineHeight: 1.7,
        }}>
          {isUser ? (
            <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {renderTextWithLinks(msg.content, { color: "rgba(255,255,255,0.9)" })}
            </span>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              className="md-content"
              components={{
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                     style={{ color: "var(--accent)", textDecoration: "underline" }}>
                    {children}
                  </a>
                )
              }}
            >
              {displayContent}
            </ReactMarkdown>
          )}
        </div>

        {/* Acciones — siempre visibles en mensajes del agente */}
        {!isUser && (
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              onClick={handleCopy}
              title={copied ? "¡Copiado!" : "Copiar respuesta"}
              style={{
                display: "flex", alignItems: "center", gap: 4,
                background: copied ? "rgba(34,197,94,0.15)" : "var(--bg-hover)",
                border: "none", cursor: "pointer",
                padding: "4px 10px", borderRadius: "var(--radius-md)",
                fontSize: 11, color: copied ? "var(--success)" : "var(--text-secondary)",
                transition: "background 0.15s, color 0.15s", fontFamily: "inherit", fontWeight: 500,
              }}
              onMouseEnter={e => { if (!copied) e.currentTarget.style.background = "var(--bg-elevated)"; }}
              onMouseLeave={e => { if (!copied) e.currentTarget.style.background = "var(--bg-hover)"; }}
            >
              {copied ? "✓ Copiado" : "⎘ Copiar"}
            </button>
            <button
              onClick={() => onExport && onExport(displayContent)}
              style={{
                display: "flex", alignItems: "center", gap: 4,
                background: "var(--bg-hover)", border: "none",
                cursor: "pointer", padding: "4px 10px", borderRadius: "var(--radius-md)",
                fontSize: 11, color: "var(--text-secondary)", fontFamily: "inherit", fontWeight: 500,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elevated)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--bg-hover)"}
            >
              ↓ Exportar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Modal de envío de email (PM agent)
function EmailSendModal({ emailData, contacts, selectedIds, onToggle, ccEmail, onCcChange, onSend, onCancel, sending }) {
  const hasContacts = contacts.length > 0;

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-surface)", borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border)", padding: 28, width: "100%", maxWidth: 520,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: "var(--text-primary)" }}>
          📬 Confirmar envío de email
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
          El email se enviará desde nahuel@creativedog.agency
        </div>

        {!hasContacts ? (
          <div style={{
            padding: "16px 20px", background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius-md)",
            fontSize: 13, color: "var(--danger)", marginBottom: 20,
          }}>
            Este cliente no tiene contactos cargados. Agregá al menos uno en el perfil del cliente antes de enviar.
          </div>
        ) : (
          <>
            {/* Destinatarios */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
                Destinatarios
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {contacts.map(c => (
                  <label key={c.id} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                    background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", cursor: "pointer",
                    border: `1px solid ${selectedIds.has(c.id) ? "var(--accent)" : "var(--border)"}`,
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => onToggle(c.id)}
                      style={{ accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.email}{c.role ? ` · ${c.role}` : ""}</div>
                    </div>
                    {c.is_primary && (
                      <span style={{ fontSize: 10, background: "rgba(34,197,94,0.15)", color: "var(--success)", padding: "2px 6px", borderRadius: 99, flexShrink: 0 }}>
                        Principal
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* CC */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>
                Copia (CC)
              </div>
              <input
                className="input"
                value={ccEmail}
                onChange={e => onCcChange(e.target.value)}
                placeholder="rolguin@creativedog.agency"
                style={{ fontSize: 13 }}
              />
            </div>
          </>
        )}

        {/* Preview */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>
            Vista previa
          </div>
          <div style={{
            padding: 14, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)", fontSize: 13,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--text-primary)" }}>
              {emailData.subject}
            </div>
            <div style={{ whiteSpace: "pre-wrap", color: "var(--text-secondary)", lineHeight: 1.6, maxHeight: 160, overflowY: "auto" }}>
              {emailData.body}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={sending}>
            Cancelar
          </button>
          {hasContacts && (
            <button
              className="btn btn-primary"
              onClick={onSend}
              disabled={sending || selectedIds.size === 0}
            >
              {sending
                ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Enviando...</>
                : "📤 Enviar email"
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal de selección de formato
function ExportModal({ onClose, onSelect }) {
  const formats = [
    { id: "txt",  label: "Texto plano",          ext: ".txt",  icon: "📄" },
    { id: "md",   label: "Markdown",             ext: ".md",   icon: "📝" },
    { id: "csv",  label: "Excel / Google Sheets", ext: ".csv",  icon: "📊" },
    { id: "html", label: "HTML",                 ext: ".html", icon: "🌐" },
    { id: "doc",  label: "Word",                 ext: ".doc",  icon: "📋" },
    { id: "pdf",  label: "PDF",                  ext: ".pdf",  icon: "📑" },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-surface)", borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border)", padding: 24, width: 320,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: "var(--text-primary)" }}>
          Exportar respuesta
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
          Elegí el formato de descarga
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {formats.map(f => (
            <button
              key={f.id}
              onClick={() => onSelect(f.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", borderRadius: "var(--radius-md)",
                background: "var(--bg-elevated)", border: "1px solid var(--border)",
                cursor: "pointer", color: "var(--text-primary)", fontFamily: "inherit",
                fontSize: 13, textAlign: "left", transition: "border-color 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
            >
              <span style={{ fontSize: 18 }}>{f.icon}</span>
              <div>
                <div style={{ fontWeight: 500 }}>{f.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{f.ext}</div>
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 16, width: "100%", padding: "8px",
            background: "none", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", color: "var(--text-muted)",
            cursor: "pointer", fontFamily: "inherit", fontSize: 13,
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// Modal de preguntas clarificadoras
function ClarifyModal({ questions, index, answers, customValue, onCustomChange, onSelect, onSkip, onClose, onNav, onCustomSubmit }) {
  const q = questions[index];
  const total = questions.length;
  const inputRef = React.useRef(null);

  // Focus the custom input when the user starts typing "Otra cosa"
  const handleCustomKeyDown = (e) => {
    if (e.key === "Enter" && customValue.trim()) {
      e.preventDefault();
      onCustomSubmit();
    }
  };

  if (!q) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: 120,
      left: "50%",
      transform: "translateX(-50%)",
      width: "min(480px, calc(100vw - 32px))",
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-xl)",
      boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
      zIndex: 500,
      overflow: "hidden",
    }}>
      {/* Header: nav + close */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px 10px",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => onNav(-1)}
            disabled={index === 0}
            style={{
              background: "none", border: "none", cursor: index === 0 ? "not-allowed" : "pointer",
              color: index === 0 ? "var(--text-muted)" : "var(--text-secondary)",
              fontSize: 14, padding: "2px 6px", borderRadius: "var(--radius-sm)",
              fontFamily: "inherit", lineHeight: 1,
            }}
          >‹</button>
          <span style={{ fontSize: 12, color: "var(--text-muted)", userSelect: "none" }}>
            {index + 1} de {total}
          </span>
          <button
            onClick={() => onNav(1)}
            disabled={index === total - 1}
            style={{
              background: "none", border: "none", cursor: index === total - 1 ? "not-allowed" : "pointer",
              color: index === total - 1 ? "var(--text-muted)" : "var(--text-secondary)",
              fontSize: 14, padding: "2px 6px", borderRadius: "var(--radius-sm)",
              fontFamily: "inherit", lineHeight: 1,
            }}
          >›</button>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: 16, padding: "2px 6px",
            borderRadius: "var(--radius-sm)", fontFamily: "inherit", lineHeight: 1,
          }}
        >✕</button>
      </div>

      {/* Question text */}
      <div style={{ padding: "16px 20px 12px", fontSize: 14, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.5 }}>
        {q.q}
      </div>

      {/* Options */}
      <div style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
        {q.opts.map((opt, i) => {
          const isSelected = answers[index] === opt;
          return (
            <button
              key={i}
              onClick={() => onSelect(opt)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 14px", borderRadius: "var(--radius-md)",
                background: isSelected ? "var(--accent)" : "var(--bg-elevated)",
                border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                cursor: "pointer", color: isSelected ? "#fff" : "var(--text-primary)",
                fontFamily: "inherit", fontSize: 13, textAlign: "left",
                transition: "background 0.12s, border-color 0.12s, color 0.12s",
              }}
              onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--text-primary)"; } }}
              onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = "var(--border)"; } }}
            >
              <span style={{
                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                background: isSelected ? "rgba(255,255,255,0.2)" : "var(--bg-hover)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 600, color: isSelected ? "#fff" : "var(--text-muted)",
              }}>{i + 1}</span>
              {opt}
            </button>
          );
        })}

        {/* Otra cosa — free text */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "7px 12px", borderRadius: "var(--radius-md)",
          background: "var(--bg-elevated)", border: "1px solid var(--border)",
          transition: "border-color 0.12s",
        }}
          onFocusCapture={e => e.currentTarget.style.borderColor = "var(--accent)"}
          onBlurCapture={e => e.currentTarget.style.borderColor = "var(--border)"}
        >
          <span style={{ fontSize: 14, color: "var(--text-muted)", flexShrink: 0 }}>✏️</span>
          <input
            ref={inputRef}
            value={customValue}
            onChange={e => onCustomChange(e.target.value)}
            onKeyDown={handleCustomKeyDown}
            placeholder="Otra cosa..."
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              color: "var(--text-primary)", fontSize: 13, fontFamily: "inherit",
            }}
          />
          {customValue.trim() && (
            <button
              onClick={onCustomSubmit}
              style={{
                background: "var(--accent)", border: "none", borderRadius: "var(--radius-sm)",
                color: "#fff", cursor: "pointer", padding: "3px 8px", fontSize: 11,
                fontFamily: "inherit", fontWeight: 600, flexShrink: 0,
              }}
            >↵</button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "10px 16px 14px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={onSkip}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: 13, fontFamily: "inherit",
            padding: "4px 8px", borderRadius: "var(--radius-sm)",
            transition: "color 0.12s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "var(--text-secondary)"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
        >
          Omitir
        </button>
      </div>
    </div>
  );
}

export default function AgentChat() {
  const { agentId } = useParams();
  const toast = useToast();
  const agent = AGENT_META[agentId];

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [extendedHistory, setExtendedHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exportModal, setExportModal] = useState(null);
  const [limitAlert, setLimitAlert] = useState(null);
  const [attachments, setAttachments] = useState([]);
  // Clarifying questions flow
  const [clarifyQuestions, setClarifyQuestions] = useState([]); // [{q, opts}]
  const [clarifyIndex, setClarifyIndex] = useState(0);
  const [clarifyAnswers, setClarifyAnswers] = useState({}); // { index: answer }
  const [clarifyCustom, setClarifyCustom] = useState("");

  // PM email send flow
  const [emailSendModal, setEmailSendModal] = useState(null); // { subject, body }
  const [pmContacts, setPmContacts] = useState([]);
  const [pmCcEmail, setPmCcEmail] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [pmSending, setPmSending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(""); // text being streamed in real-time
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    API.get("/api/clients").then(r => setClients([...r.data].filter(c => c.status === "active").sort((a, b) => a.name.localeCompare(b.name, "es"))));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingMessage]);

  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setSelectedClient(null);
    setInput("");
    setClarifyQuestions([]);
    setClarifyAnswers({});
    setClarifyCustom("");
  }, [agentId]);

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setMessages([]);
    setConversationId(null);
    setLimitAlert(null);
    setLoadingHistory(true);
    try {
      const r = await API.get(`/api/agents/${agentId}/conversations/${client.id}`);
      if (r.data.length > 0) {
        const latest = r.data[0];
        setMessages(latest.messages || []);
        setConversationId(latest.id);
      }
    } catch {}
    finally { setLoadingHistory(false); }
  };

  const handleExtendedHistory = async () => {
    if (!selectedClient) return;
    setExtendedHistory(true);
    setLoadingHistory(true);
    try {
      const r = await API.get(`/api/agents/${agentId}/conversations/${selectedClient.id}?extended=true`);
      if (r.data.length > 0) {
        const latest = r.data[0];
        setMessages(latest.messages || []);
        setConversationId(latest.id);
        toast("Historial completo cargado", "success");
      }
    } catch {
      toast("Error al cargar historial", "error");
    } finally { setLoadingHistory(false); }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    for (const file of files) {
      const id = `${Date.now()}-${Math.random()}`;
      setAttachments(prev => [...prev, { id, filename: file.name, loading: true }]);
      try {
        const form = new FormData();
        form.append("file", file);
        const r = await API.post("/api/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments(prev => prev.map(a => a.id === id ? { ...a, loading: false, ...r.data } : a));
      } catch (err) {
        toast(describeUploadError(err, file.name), "error");
        setAttachments(prev => prev.filter(a => a.id !== id));
      }
    }
  };

  // When the SSE stream dies mid-response (proxy timeout, dropped connection),
  // preserve whatever text Claude already generated instead of silently
  // discarding it — the user's question stays visible either way.
  const appendIncompleteResponse = (accumulated) => {
    if (!accumulated?.trim()) return;
    setMessages(prev => [...prev, {
      role: "assistant",
      content: `${accumulated}\n\n_[Respuesta incompleta — se cortó la conexión. Pediselo de nuevo si falta información.]_`,
    }]);
  };

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || loading || !selectedClient) return;
    const readyAttachments = attachments.filter(a => !a.loading);
    const userMessage = {
      role: "user",
      content: input.trim(),
      ...(readyAttachments.length > 0 && {
        attachments: readyAttachments.map(a => ({
          filename: a.filename, url: a.url, type: a.type,
          uploadedAt: new Date().toISOString(),
        })),
      }),
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setAttachments([]);
    setLoading(true);
    setStreamingMessage("");
    abortControllerRef.current = new AbortController();

    const token = localStorage.getItem("smith_token");
    const body = JSON.stringify({
      clientId: selectedClient.id,
      messages: newMessages,
      conversationId,
      extendedHistory,
      attachments: readyAttachments,
    });

    try {
      const response = await fetch(`/api/agents/${agentId}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body,
        signal: abortControllerRef.current.signal,
      });

      // Pre-stream HTTP errors (limit exceeded, 404, etc.) come as JSON
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429 && errData.error === "global_monthly_limit_exceeded") {
          setLimitAlert({ scope: "global", current_usd: errData.current_usd, limit_usd: errData.limit_usd });
        } else if (response.status === 429 && errData.error === "monthly_limit_exceeded") {
          setLimitAlert({ scope: "client", current_usd: errData.current_usd, limit_usd: errData.limit_usd });
        } else {
          toast(errData.error || "Error al enviar mensaje", "error");
        }
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === "chunk") {
            accumulated += event.text;
            setStreamingMessage(accumulated);
          } else if (event.type === "done") {
            setStreamingMessage("");
            const assistantContent = accumulated;

            // PM agent: intercept [SEND_EMAIL] trigger
            if (agentId === "pm" && assistantContent.includes("[SEND_EMAIL]")) {
              const cleanContent = assistantContent.replace("[SEND_EMAIL]", "").trim();
              setMessages(prev => [...prev, { role: "assistant", content: cleanContent }]);
              if (!conversationId) setConversationId(event.conversationId);
              const emailData = parseEmailBlock(newMessages);
              if (emailData) {
                try {
                  const contactsRes = await API.get(`/api/clients/${selectedClient.id}/contacts`);
                  const contacts = contactsRes.data;
                  const cc = selectedClient.project_leader_email || "rolguin@creativedog.agency";
                  setPmContacts(contacts);
                  setPmCcEmail(cc);
                  setSelectedContactIds(new Set(contacts.filter(c => c.is_primary).map(c => c.id)));
                  setEmailSendModal(emailData);
                } catch {
                  toast("Error al cargar contactos", "error");
                }
              }
            } else {
              setMessages(prev => [...prev, { role: "assistant", content: assistantContent }]);
              if (!conversationId) setConversationId(event.conversationId);
              if (event.questions?.length > 0) {
                setClarifyQuestions(event.questions);
                setClarifyIndex(0);
                setClarifyAnswers({});
                setClarifyCustom("");
              }
            }
          } else if (event.type === "error") {
            toast(event.error || "Error al generar respuesta", "error");
            // No se borra el mensaje del usuario: ya se envió y consumió
            // presupuesto de la API — ocultarlo hace parecer que el chat
            // "nunca pasó nada". Si ya había texto parcial, se conserva.
            appendIncompleteResponse(accumulated);
            setStreamingMessage("");
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        // Cancelación deliberada del usuario — acá sí tiene sentido revertir
        setMessages(prev => prev.slice(0, -1));
        setStreamingMessage("");
        return;
      }
      // Conexión cortada a mitad de stream (timeout de proxy, red, etc.) —
      // mismo criterio: nunca borrar el mensaje del usuario ni el texto
      // parcial ya generado.
      toast("Se cortó la conexión mientras Claude respondía. Probá de nuevo.", "error");
      appendIncompleteResponse(accumulated);
      setStreamingMessage("");
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleToggleContact = (id) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleEmailSend = async () => {
    const selected = pmContacts.filter(c => selectedContactIds.has(c.id));
    if (selected.length === 0) { toast("Seleccioná al menos un destinatario", "error"); return; }
    setPmSending(true);
    try {
      await API.post("/api/pm/send-email", {
        clientId: selectedClient.id,
        subject: emailSendModal.subject,
        body: emailSendModal.body,
        recipientEmails: selected.map(c => c.email),
        ccEmail: pmCcEmail || null,
      });
      toast("Email enviado correctamente", "success");
      const toList = selected.map(c => c.email).join(", ");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `✅ Email enviado a ${toList}${pmCcEmail ? ` con copia a ${pmCcEmail}` : ""}`,
      }]);
      setEmailSendModal(null);
    } catch (err) {
      toast(err.response?.data?.error || "Error al enviar el email", "error");
    } finally {
      setPmSending(false);
    }
  };

  // Build and send compiled answers from clarifying questions
  const sendClarifyAnswers = (answers) => {
    const answered = Object.entries(answers);
    if (answered.length === 0) return;
    const lines = answered.map(([idx, ans]) => {
      const q = clarifyQuestions[parseInt(idx)];
      return `• ${q?.q || ""}: ${ans}`;
    });
    const compiled = `Mis respuestas:\n${lines.join("\n")}`;
    setInput(compiled);
    // Auto-send
    setTimeout(() => {
      setMessages(prev => [...prev, { role: "user", content: compiled }]);
      setInput("");
      setLoading(true);
      const newMessages = [...messages, { role: "user", content: compiled }];
      abortControllerRef.current = new AbortController();
      API.post(`/api/agents/${agentId}/chat`, {
        clientId: selectedClient.id,
        messages: newMessages,
        conversationId,
        extendedHistory,
        attachments: [],
      }, { signal: abortControllerRef.current.signal })
        .then(r => {
          setMessages(prev => [...prev, { role: "assistant", content: r.data.message }]);
          if (!conversationId) setConversationId(r.data.conversationId);
          if (r.data.questions?.length > 0) {
            setClarifyQuestions(r.data.questions);
            setClarifyIndex(0);
            setClarifyAnswers({});
            setClarifyCustom("");
          }
        })
        .catch(err => {
          if (err.code !== "ERR_CANCELED" && err.name !== "CanceledError" && err.name !== "AbortError") {
            toast(err.response?.data?.error || "Error al enviar", "error");
          }
        })
        .finally(() => { setLoading(false); abortControllerRef.current = null; });
    }, 0);
  };

  const handleClarifySelect = (answer) => {
    const newAnswers = { ...clarifyAnswers, [clarifyIndex]: answer };
    setClarifyAnswers(newAnswers);
    setClarifyCustom("");
    const isLast = clarifyIndex === clarifyQuestions.length - 1;
    if (isLast) {
      setClarifyQuestions([]);
      sendClarifyAnswers(newAnswers);
    } else {
      setClarifyIndex(clarifyIndex + 1);
    }
  };

  const handleClarifySkip = () => {
    const isLast = clarifyIndex === clarifyQuestions.length - 1;
    if (isLast) {
      setClarifyQuestions([]);
      sendClarifyAnswers(clarifyAnswers);
    } else {
      setClarifyIndex(clarifyIndex + 1);
    }
  };

  const handleClarifyClose = () => {
    setClarifyQuestions([]);
    sendClarifyAnswers(clarifyAnswers);
  };

  const handleClarifyNav = (dir) => {
    const next = clarifyIndex + dir;
    if (next >= 0 && next < clarifyQuestions.length) setClarifyIndex(next);
  };

  const handleExportFormat = (format) => {
    if (!exportModal) return;
    exportContent(exportModal.content, format, agent.name, selectedClient?.name || "cliente");
    setExportModal(null);
    toast("Documento descargado", "success");
  };

  if (!agent) return <div style={{ padding: 36, color: "var(--text-muted)" }}>Agente no encontrado</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px",
        borderBottom: "1px solid var(--border)",
        background: "var(--bg-surface)",
        flexShrink: 0,
      }}>
        <div className="agent-chat-header" style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          {/* Agent info — izquierda */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "var(--radius-md)", flexShrink: 0,
              background: agent.color + "22", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 16,
            }}>
              {agent.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{agent.name}</div>
              {selectedClient && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Cliente: <span style={{ color: agent.color }}>{selectedClient.name}</span>
                </div>
              )}
            </div>
            {extendedHistory && (
              <span style={{ fontSize: 11, color: "var(--warning)", background: "rgba(245,158,11,0.1)", padding: "3px 8px", borderRadius: 99, flexShrink: 0 }}>
                Historial completo
              </span>
            )}
          </div>

          {/* Cambiar cliente — solo visible cuando hay un cliente seleccionado */}
          {selectedClient && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div style={{ width: "clamp(140px, 18%, 240px)" }}>
                <ClientSearch
                  clients={clients}
                  selected={selectedClient}
                  onSelect={handleClientSelect}
                />
              </div>
              {!extendedHistory && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleExtendedHistory}
                  disabled={loadingHistory}
                  title="Cargar historial completo"
                  style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0, padding: "6px" }}
                >
                  📚
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {!selectedClient ? (
          <div className="empty-state">
            <div style={{ fontSize: 40, marginBottom: 16 }}>{agent.icon}</div>
            <h3>{agent.name}</h3>
            <p style={{ marginTop: 8, marginBottom: 24 }}>Seleccioná un cliente para comenzar</p>
            <div style={{ width: "100%", maxWidth: 300, margin: "0 auto" }}>
              <ClientSearch
                clients={clients}
                selected={selectedClient}
                onSelect={handleClientSelect}
              />
            </div>
          </div>
        ) : loadingHistory ? (
          <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 40, marginBottom: 16 }}>{agent.icon}</div>
            <h3>Nueva conversación</h3>
            <p style={{ marginTop: 8 }}>Hablando con {selectedClient.name}</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 28, maxWidth: "80%", margin: "0 auto" }}>
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={msg}
                agent={agent}
                onExport={(content) => setExportModal({ content })}
              />
            ))}
            {loading && streamingMessage ? (
              <MessageBubble
                key="streaming"
                msg={{ role: "assistant", content: streamingMessage }}
                agent={agent}
                onExport={() => {}}
                streaming
              />
            ) : loading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", background: agent.color + "22",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                }}>
                  {agent.icon}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted)",
                      animation: "pulse 1.2s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                    }} />
                  ))}
                </div>
              </div>
            ) : null}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Limit alert banner */}
      {limitAlert && (
        <div style={{
          padding: "14px 24px",
          borderTop: "1px solid rgba(239,68,68,0.3)",
          background: "rgba(239,68,68,0.08)",
          flexShrink: 0,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>🚫</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--danger)", marginBottom: 2 }}>
              {limitAlert.scope === "global" ? "Sin créditos disponibles este mes" : "Límite mensual alcanzado"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              {limitAlert.scope === "global" ? (
                <>Se alcanzó el límite mensual de uso de la plataforma (<strong>USD ${parseFloat(limitAlert.limit_usd).toFixed(2)}</strong>).
                Por favor contactá con soporte para ampliar el límite y seguir usando los agentes.</>
              ) : (
                <>Este cliente llegó a su límite de <strong>USD ${parseFloat(limitAlert.limit_usd).toFixed(2)}</strong> este mes
                (consumo actual: USD ${parseFloat(limitAlert.current_usd).toFixed(4)}).
                Contactate con el administrador para aumentar el límite.</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      {selectedClient && !limitAlert && (
        <div style={{
          padding: "16px 24px",
          borderTop: "1px solid var(--border)",
          background: "var(--bg-surface)",
          flexShrink: 0,
        }}>
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            {/* Attachment chips */}
            {attachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {attachments.map(a => {
                  const icon = a.type === "image" ? "🖼️" : a.filename?.endsWith(".pdf") ? "📄" : a.filename?.match(/\.docx?$/) ? "📋" : a.filename?.match(/\.csv|\.xlsx?$/) ? "📊" : "📝";
                  return (
                    <div key={a.id} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "var(--bg-elevated)", border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)", padding: "4px 8px 4px 10px",
                      fontSize: 12, color: "var(--text-secondary)", maxWidth: 200,
                    }}>
                      {a.loading
                        ? <div className="spinner" style={{ width: 10, height: 10, borderWidth: 1.5, flexShrink: 0 }} />
                        : <span style={{ fontSize: 13 }}>{icon}</span>
                      }
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {a.filename}
                      </span>
                      <button
                        onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                      >×</button>
                    </div>
                  );
                })}
              </div>
            )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.pptx,.ppt,.txt,.md,.rtf,.csv,.json,.xml,.svg,.xlsx,.xls,.jpg,.jpeg,.png,.webp,.gif"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <div style={{
            display: "flex", gap: 10,
            background: "var(--bg-elevated)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)", padding: "8px 8px 8px 8px",
          }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Adjuntar archivo"
              disabled={loading}
              style={{
                width: 32, height: 32, borderRadius: "var(--radius-md)",
                background: "none", border: "none", cursor: loading ? "not-allowed" : "pointer",
                color: "var(--text-muted)", fontSize: 16, display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0, alignSelf: "flex-end", marginBottom: 2,
                transition: "color 0.15s",
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.color = "var(--text-secondary)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; }}
            >📎</button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
              }}
              onKeyDown={handleKeyDown}
              placeholder={`Escribile a ${agent.name}...`}
              rows={1}
              style={{
                flex: 1, background: "none", border: "none", outline: "none",
                resize: "none", color: "var(--text-primary)", fontSize: 14,
                fontFamily: "DM Sans, sans-serif", lineHeight: 1.6, paddingTop: 6, maxHeight: 160,
              }}
            />
            <button
              onClick={loading ? handleStop : handleSend}
              disabled={!loading && !input.trim() && attachments.filter(a => !a.loading).length === 0}
              title={loading ? "Detener generación" : "Enviar"}
              style={{
                width: 36, height: 36, borderRadius: "var(--radius-md)",
                background: loading ? "rgba(239,68,68,0.15)" : (input.trim() || attachments.length > 0 ? "var(--accent)" : "var(--bg-hover)"),
                border: loading ? "1px solid rgba(239,68,68,0.4)" : "none",
                color: loading ? "var(--danger)" : "#fff",
                cursor: loading || input.trim() || attachments.length > 0 ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, alignSelf: "flex-end", marginBottom: 2, transition: "background 0.15s",
                fontSize: loading ? 12 : 14, fontWeight: 700,
              }}
            >
              {loading ? "■" : "↑"}
            </button>
          </div>
          </div>
          <p style={{ textAlign: "center", fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
            Enter para enviar · Shift+Enter para nueva línea · 📎 para adjuntar archivos
          </p>
        </div>
      )}

      {exportModal && (
        <ExportModal
          onClose={() => setExportModal(null)}
          onSelect={handleExportFormat}
        />
      )}

      {emailSendModal && (
        <EmailSendModal
          emailData={emailSendModal}
          contacts={pmContacts}
          selectedIds={selectedContactIds}
          onToggle={handleToggleContact}
          ccEmail={pmCcEmail}
          onCcChange={setPmCcEmail}
          onSend={handleEmailSend}
          onCancel={() => setEmailSendModal(null)}
          sending={pmSending}
        />
      )}

      {clarifyQuestions.length > 0 && (
        <ClarifyModal
          questions={clarifyQuestions}
          index={clarifyIndex}
          answers={clarifyAnswers}
          customValue={clarifyCustom}
          onCustomChange={setClarifyCustom}
          onSelect={handleClarifySelect}
          onSkip={handleClarifySkip}
          onClose={handleClarifyClose}
          onNav={handleClarifyNav}
          onCustomSubmit={() => clarifyCustom.trim() && handleClarifySelect(clarifyCustom.trim())}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @media (max-width: 640px) {
          .agent-header-selector { width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
